import { supabase } from './supabaseClient.js';
import { logError } from '../lib/utils.js';

/**
 * Servicio para gestionar contrataciones desde Supabase
 * Maneja datos de contratantes, cotizaciones y cargas familiares
 */
class ContratantesService {
  /**
   * Obtiene todas las contrataciones con información relacionada
   * @param {Object} options - Opciones de filtrado y paginación
   * @returns {Promise<Object>} Lista de contrataciones con metadatos
   */
  async getContrataciones(options = {}) {
    try {
      const {
        page = 1,
        limit = 50,
        estado = null,
        soloConSuscripcion = false,
        search = '',
        sortBy = 'created_at',
        sortOrder = 'desc'
      } = options;

      // Si soloConSuscripcion, agrupamos por SUSCRIPCIÓN: la lista muestra un
      // contratante (una suscripción) por fila, aunque haya contratado varios
      // planes a la vez. Cada suscripción tiene una contratación "representante"
      // (la primera de contrataciones_data / la del contratacion_id directo) y
      // conocemos todas sus contrataciones para el badge "N planes". La
      // trazabilidad ya despliega todos los planes a partir de cualquier id.
      let contratacionIdsConSuscripcion = null;
      // Mapa contratacion_id representante → { codigo, numPlanes, certificadoCodigos }
      this._subInfoMap = {};
      if (soloConSuscripcion) {
        const SUSCRIPCION_ACTIVA = ['activa', 'confirmada', 'suscribiendo'];
        const { data: subs } = await supabase
          .from('subscriptions')
          .select('id, contratacion_id, contrataciones_data, codigo, certificado_id')
          .in('status', SUSCRIPCION_ACTIVA);

        const ids = new Set();
        // contratacion_id representante → [certificado_id, ...] de todos sus planes.
        // Se recogen aquí y se resuelven a códigos en UNA query al final: hacerlo
        // por fila serían N round-trips.
        const certIdsPorRepresentante = {};
        // contratacion_id representante → subscription.id. Necesario porque el
        // vínculo principal es `certificados.subscription_id`; `certificado_id`
        // en la suscripción solo está en parte de los datos (misma cadena de
        // fallback que usa getTrazabilidad más abajo).
        const subIdPorRepresentante = {};

        for (const s of subs || []) {
          const arr = Array.isArray(s.contrataciones_data)
            ? s.contrataciones_data
            : (typeof s.contrataciones_data === 'string' ? (() => { try { return JSON.parse(s.contrataciones_data); } catch { return []; } })() : []);

          if (Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'object') {
            // Formato nuevo: la suscripción agrupa varias contrataciones (planes).
            // Representante = primera con contratacion_id; numPlanes = total válidas.
            const conId = arr.map(c => c?.contratacion_id).filter(Boolean);
            const first = conId[0];
            if (first) {
              ids.add(first);
              this._subInfoMap[first] = {
                codigo: s.codigo || null,
                numPlanes: conId.length,
              };
              // Cada plan puede tener su propio certificado.
              certIdsPorRepresentante[first] = arr.map(c => c?.certificado_id).filter(Boolean);
              if (s.id) subIdPorRepresentante[first] = s.id;
            }
          } else if (s.contratacion_id) {
            // Formato antiguo: contratacion_id directo (un solo plan).
            ids.add(s.contratacion_id);
            this._subInfoMap[s.contratacion_id] = {
              codigo: s.codigo || null,
              numPlanes: 1,
            };
            // Aquí el certificado cuelga de la suscripción, no de los planes.
            certIdsPorRepresentante[s.contratacion_id] = s.certificado_id ? [s.certificado_id] : [];
            if (s.id) subIdPorRepresentante[s.contratacion_id] = s.id;
          }
        }
        contratacionIdsConSuscripcion = [...ids];
        if (contratacionIdsConSuscripcion.length === 0) {
          return { data: [], count: 0, page, limit, totalPages: 0 };
        }

        // Resolver los códigos de certificado en dos consultas (no N), para que
        // estén en la fila y el buscador de la tabla los alcance.
        //
        // Hay dos vías porque los datos no son homogéneos:
        //   a) `certificado_id` en la suscripción o en cada plan
        //   b) `certificados.subscription_id` apuntando a la suscripción
        // Es la misma cadena de fallback de `getTrazabilidad`. Con (a) sola se
        // perdían los certificados de casi todas las filas.
        const todosCertIds = [...new Set(Object.values(certIdsPorRepresentante).flat())];
        const todosSubIds  = [...new Set(Object.values(subIdPorRepresentante))];

        const [porId, porSub] = await Promise.all([
          todosCertIds.length > 0
            ? supabase.from('certificados').select('id, codigo').in('id', todosCertIds)
            : Promise.resolve({ data: [] }),
          todosSubIds.length > 0
            ? supabase.from('certificados').select('codigo, subscription_id').in('subscription_id', todosSubIds)
            : Promise.resolve({ data: [] }),
        ]);

        const codigoPorId = Object.fromEntries((porId.data || []).map(c => [c.id, c.codigo]));
        // Una suscripción puede tener varios certificados (uno por plan).
        const codigosPorSubId = {};
        for (const c of porSub.data || []) {
          if (!c.subscription_id || !c.codigo) continue;
          (codigosPorSubId[c.subscription_id] ||= []).push(c.codigo);
        }

        for (const repId of Object.keys(this._subInfoMap)) {
          const desdeIds = (certIdsPorRepresentante[repId] || [])
            .map(id => codigoPorId[id]).filter(Boolean);
          const desdeSub = codigosPorSubId[subIdPorRepresentante[repId]] || [];
          // Ambas vías pueden apuntar al mismo certificado: deduplicar.
          this._subInfoMap[repId].certificadoCodigos = [...new Set([...desdeIds, ...desdeSub])];
        }
      }

      let query = supabase
        .from('contrataciones')
        .select(`
          *,
          actividades:actividad_id(nombre, factor_riesgo),
          cotizaciones:cotizacion_id(
            id,
            plan_id,
            cotizacion_lineas(
              precio_uf,
              descuento_aplicado,
              cobertura_id
            )
          ),
          contratacion_cargas(
            id,
            nombre,
            apellido_paterno,
            apellido_materno,
            rut,
            tipo,
            edad,
            sexo,
            prevision,
            fecha_nacimiento,
            nacionalidad,
            datos_carga
          )
        `);

      // Filtrar solo contrataciones con suscripción activa
      if (contratacionIdsConSuscripcion) {
        query = query.in('id', contratacionIdsConSuscripcion);
      }

      // Filtro por estado
      if (estado && estado !== 'todos') {
        query = query.eq('estado', estado);
      }

      // Búsqueda por nombre, email o RUT
      if (search) {
        query = query.or(`nombre.ilike.%${search}%,mail.ilike.%${search}%,rut.ilike.%${search}%,rut_titular.ilike.%${search}%`);
      }

      // Ordenamiento
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Paginación
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        logError('Error al obtener contrataciones:', error, { ignoreAbort: true });
        throw new Error(`Error al cargar contrataciones: ${error.message}`);
      }

      // Obtener compania_nombre desde la vista company_contrataciones (tiene
      // permisos donde companias no). La lista ya no muestra la compañía (una
      // suscripción puede agrupar planes de varias); se resuelve solo la de la
      // contratación representante para consumidores que aún lean el campo.
      // La compañía correcta de cada plan se ve en la trazabilidad.
      const contratacionIds = (data || []).map(c => c.id).filter(Boolean);
      const companiaNombreMap = {};
      if (contratacionIds.length > 0) {
        const { data: vistaData } = await supabase
          .from('company_contrataciones')
          .select('id, compania_nombre')
          .in('id', contratacionIds);
        (vistaData || []).forEach(c => { if (c.compania_nombre) companiaNombreMap[c.id] = c.compania_nombre; });
      }

      // Procesar datos para incluir información calculada
      const processedData = data?.map(contratacion => {
        const datosTitular = (() => {
          const raw = contratacion.datos_titular;
          if (!raw) return {};
          if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return {}; } }
          return raw;
        })();

        return {
          ...contratacion,
          nombre_completo: contratacion.nombre || 'Sin nombre',
          total_cargas: contratacion.contratacion_cargas?.length || 0,
          valor_total_uf: this.calcularValorTotal(contratacion.cotizaciones?.cotizacion_lineas || []),
          dias_para_vencer: this.calcularDiasParaVencer(contratacion.expires_at),
          estado_badge: this.getEstadoBadge(contratacion.estado),
          compania_nombre: companiaNombreMap[contratacion.id] || 'Sin Compañía',
          codigo_suscripcion: this._subInfoMap?.[contratacion.id]?.codigo || null,
          num_planes: this._subInfoMap?.[contratacion.id]?.numPlanes || 1,
          // Códigos de certificado de todos los planes de la suscripción.
          // `certificados_codigos` (string) es lo que busca YoolTable, que filtra
          // sobre valores planos; el array queda disponible para la UI.
          certificados: this._subInfoMap?.[contratacion.id]?.certificadoCodigos || [],
          certificados_codigos: (this._subInfoMap?.[contratacion.id]?.certificadoCodigos || []).join(' '),
          telefono: contratacion.telefono ||
                   datosTitular?.telefono ||
                   null,
        };
      }) || [];

      return {
        data: processedData,
        count: count || 0,
        page,
        limit,
        totalPages: Math.ceil((count || 0) / limit)
      };

    } catch (error) {
      logError('Error en getContrataciones:', error, { ignoreAbort: true });
      throw error;
    }
  }

  /**
   * Obtiene una contratación específica por ID
   * @param {string} id - ID de la contratación
   * @returns {Promise<Object>} Datos completos de la contratación
   */
  async getContratacionById(id) {
    try {
      const { data, error } = await supabase
        .from('contrataciones')
        .select(`
          *,
          actividades:actividad_id(nombre, factor_riesgo),
          cotizaciones:cotizacion_id(
            *,
            cotizacion_lineas(
              *,
              coberturas:cobertura_id(nombre, descripcion)
            )
          ),
          contratacion_cargas(
            *
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al obtener contratación:', error);
        throw new Error(`Error al cargar contratación: ${error.message}`);
      }

      return {
        ...data,
        valor_total_uf: this.calcularValorTotal(data.cotizaciones?.cotizacion_lineas || []),
        dias_para_vencer: this.calcularDiasParaVencer(data.expires_at)
      };

    } catch (error) {
      console.error('Error en getContratacionById:', error);
      throw error;
    }
  }

  /**
   * Obtiene estadísticas de contrataciones
   * @returns {Promise<Object>} Estadísticas generales
   */
  async getEstadisticas() {
    try {
      const { data, error } = await supabase
        .from('contrataciones')
        .select('estado, expires_at');

      if (error) {
        logError('Error al obtener estadísticas:', error, { ignoreAbort: true });
        throw new Error(`Error al cargar estadísticas: ${error.message}`);
      }

      const total = data?.length || 0;
      const pendientes = data?.filter(c => c.estado === 'pendiente').length || 0;
      const aprobadas = data?.filter(c => c.estado === 'aprobada').length || 0;
      const rechazadas = data?.filter(c => c.estado === 'rechazada').length || 0;
      const expiradas = data?.filter(c => 
        c.expires_at && new Date(c.expires_at) < new Date()
      ).length || 0;

      return {
        total,
        pendientes,
        aprobadas,
        rechazadas,
        expiradas,
        activas: aprobadas
      };

    } catch (error) {
      logError('Error en getEstadisticas:', error, { ignoreAbort: true });
      throw error;
    }
  }

  /**
   * Actualiza el estado de una contratación
   * @param {string} id - ID de la contratación
   * @param {string} nuevoEstado - Nuevo estado
   * @returns {Promise<Object>} Contratación actualizada
   */
  async actualizarEstado(id, nuevoEstado) {
    try {
      const { data, error } = await supabase
        .from('contrataciones')
        .update({ 
          estado: nuevoEstado,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar estado:', error);
        throw new Error(`Error al actualizar estado: ${error.message}`);
      }

      return data;

    } catch (error) {
      console.error('Error en actualizarEstado:', error);
      throw error;
    }
  }

  /**
   * Actualiza un contratante: campos base de `contrataciones`, el jsonb
   * `datos_titular` y sincroniza las `contratacion_cargas` (upsert + borrado
   * de las que se quitaron en el formulario).
   * @param {string} id - ID de la contratación
   * @param {Object} formData - { nombre, rut, mail, telefono, estado, datos_titular, contratacion_cargas }
   */
  async updateContratante(id, formData) {
    try {
      const { contratacion_cargas = [], datos_titular = {}, ...base } = formData;

      // Nombre "display" de la contratación derivado del titular si no viene aparte.
      const nombreTitular = [datos_titular.nombre, datos_titular.apellido_paterno, datos_titular.apellido_materno]
        .filter(Boolean).join(' ').trim();

      // 1. Actualizar la contratación (campos base + jsonb titular).
      const { error: updErr } = await supabase
        .from('contrataciones')
        .update({
          nombre: base.nombre || nombreTitular || null,
          rut: base.rut || datos_titular.rut || null,
          mail: base.mail || datos_titular.email || null,
          telefono: base.telefono || datos_titular.telefono || null,
          estado: base.estado || 'pendiente',
          datos_titular,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updErr) throw new Error(`Error al actualizar contratante: ${updErr.message}`);

      // 2. Sincronizar cargas familiares.
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const limpiar = (c) => ({
        contratacion_id: id,
        tipo: c.tipo || 'carga',
        nombre: c.nombre || null,
        apellido_paterno: c.apellido_paterno || null,
        apellido_materno: c.apellido_materno || null,
        rut: c.rut || null,
        edad: c.edad === '' || c.edad == null ? null : Number(c.edad),
        sexo: c.sexo || null,
        prevision: c.prevision || null,
        fecha_nacimiento: c.fecha_nacimiento || null,
        updated_at: new Date().toISOString(),
      });

      // Cargas existentes en DB para detectar cuáles borrar.
      const { data: existentes } = await supabase
        .from('contratacion_cargas')
        .select('id')
        .eq('contratacion_id', id);
      const idsExistentes = new Set((existentes || []).map((c) => c.id));

      const conservadas = new Set();
      for (const carga of contratacion_cargas) {
        // id UUID válido = fila existente → update; si no, es nueva → insert.
        if (carga.id && UUID_RE.test(String(carga.id))) {
          conservadas.add(carga.id);
          const { error } = await supabase
            .from('contratacion_cargas')
            .update(limpiar(carga))
            .eq('id', carga.id);
          if (error) throw new Error(`Error al actualizar carga: ${error.message}`);
        } else {
          const { error } = await supabase
            .from('contratacion_cargas')
            .insert(limpiar(carga));
          if (error) throw new Error(`Error al crear carga: ${error.message}`);
        }
      }

      // Borrar las cargas que ya no están en el formulario.
      const aBorrar = [...idsExistentes].filter((cid) => !conservadas.has(cid));
      if (aBorrar.length > 0) {
        const { error } = await supabase
          .from('contratacion_cargas')
          .delete()
          .in('id', aBorrar);
        if (error) throw new Error(`Error al eliminar cargas: ${error.message}`);
      }

      return { id };
    } catch (error) {
      console.error('Error en updateContratante:', error);
      throw error;
    }
  }

  /**
   * Crea una contratación manual (alta desde el admin).
   * NOTA: una contratación normalmente nace del flujo de compra (cotización,
   * carrito, precio_base). Este alta manual inserta solo los datos del titular y
   * las cargas; queda sin cotizacion_id/plan asociado. Úsese con cuidado.
   */
  async createContratante(formData) {
    try {
      const { contratacion_cargas = [], datos_titular = {}, ...base } = formData;
      const nombreTitular = [datos_titular.nombre, datos_titular.apellido_paterno, datos_titular.apellido_materno]
        .filter(Boolean).join(' ').trim();

      const { data: nueva, error: insErr } = await supabase
        .from('contrataciones')
        .insert({
          nombre: base.nombre || nombreTitular || null,
          rut: base.rut || datos_titular.rut || null,
          mail: base.mail || datos_titular.email || null,
          telefono: base.telefono || datos_titular.telefono || null,
          estado: base.estado || 'pendiente',
          datos_titular,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (insErr) throw new Error(`Error al crear contratante: ${insErr.message}`);

      if (contratacion_cargas.length > 0) {
        const filas = contratacion_cargas.map((c) => ({
          contratacion_id: nueva.id,
          tipo: c.tipo || 'carga',
          nombre: c.nombre || null,
          apellido_paterno: c.apellido_paterno || null,
          apellido_materno: c.apellido_materno || null,
          rut: c.rut || null,
          edad: c.edad === '' || c.edad == null ? null : Number(c.edad),
          sexo: c.sexo || null,
          prevision: c.prevision || null,
          fecha_nacimiento: c.fecha_nacimiento || null,
        }));
        const { error: cargasErr } = await supabase.from('contratacion_cargas').insert(filas);
        if (cargasErr) throw new Error(`Error al crear cargas: ${cargasErr.message}`);
      }

      return nueva;
    } catch (error) {
      console.error('Error en createContratante:', error);
      throw error;
    }
  }

  /**
   * Calcula el valor total en UF de las líneas de cotización
   * @param {Array} lineas - Array de líneas de cotización
   * @returns {number} Valor total en UF
   */
  calcularValorTotal(lineas) {
    if (!Array.isArray(lineas)) return 0;
    
    return lineas.reduce((total, linea) => {
      const precio = linea.precio_uf || 0;
      const descuento = linea.descuento_aplicado || 0;
      return total + (precio - descuento);
    }, 0);
  }

  /**
   * Calcula los días restantes para vencer
   * @param {string} expiresAt - Fecha de expiración
   * @returns {number} Días para vencer (negativo si ya expiró)
   */
  calcularDiasParaVencer(expiresAt) {
    if (!expiresAt) return null;
    
    const ahora = new Date();
    const expira = new Date(expiresAt);
    const diferencia = expira.getTime() - ahora.getTime();
    
    return Math.ceil(diferencia / (1000 * 60 * 60 * 24));
  }

  /**
   * Obtiene la configuración del badge según el estado
   * @param {string} estado - Estado de la contratación
   * @returns {Object} Configuración del badge
   */
  getEstadoBadge(estado) {
    const badges = {
      'pendiente': { color: 'yellow', text: 'Pendiente' },
      'aprobada': { color: 'green', text: 'Aprobada' },
      'rechazada': { color: 'red', text: 'Rechazada' },
      'expirada': { color: 'gray', text: 'Expirada' },
      'en_proceso': { color: 'blue', text: 'En Proceso' }
    };
    
    return badges[estado] || { color: 'gray', text: estado || 'Sin estado' };
  }

  /**
   * Obtiene los estados disponibles para filtros
   * @returns {Array} Lista de estados
   */
  getEstadosDisponibles() {
    return [
      { value: 'todos', label: 'Todos los estados' },
      { value: 'pendiente', label: 'Pendiente' },
      { value: 'aprobada', label: 'Aprobada' },
      { value: 'rechazada', label: 'Rechazada' },
      { value: 'expirada', label: 'Expirada' },
      { value: 'en_proceso', label: 'En Proceso' }
    ];
  }

  /**
   * Alias para getEstadisticas para compatibilidad
   */
  async getStats() {
    return this.getEstadisticas();
  }

  /**
   * Carga la trazabilidad completa de un contratante para la CMF.
   * Cadena: contratacion → cotizacion → sesion (IP, canal, simulacion, DPS)
   *                                    ↘ suscripcion → certificado
   * @param {string} contratacionId
   * @param {object} client - cliente Supabase con auth de Clerk (createClerkSupabaseClient)
   */
  async getTrazabilidad(contratacionId, client) {
    const db = client || supabase;
    try {
      // 1. Traer contratacion con cotizacion. La sesion la buscamos aparte
      //    porque el join anidado cotizaciones→sesiones falla con RLS cuando
      //    el cliente anónimo no tiene permisos sobre la tabla sesiones.
      const { data: ct, error: ctError } = await db
        .from('contrataciones')
        .select(`
          id, codigo, created_at, estado, nombre, rut, mail, datos_titular,
          subscription_id,
          cotizaciones:cotizacion_id(
            id, codigo, created_at, resumen, expires_at, sesion_id,
            planes:plan_id(nombre, producto:producto_id(nombre, companias:compania_id(nombre_corto)))
          )
        `)
        .eq('id', contratacionId)
        .single();

      if (ctError) throw ctError;

      const cotizacion = ct.cotizaciones;
      const sesionId = cotizacion?.sesion_id;

      // 2. Buscar suscripcion por contratacion_id (la FK real es subscriptions.contratacion_id)
      //    y también intentar por ct.subscription_id como fallback
      const [sesionRes, subRes] = await Promise.all([
        sesionId
          ? db.from('sesiones_funnel')
              .select('id, created_at, ip, canal, user_agent, datos_simulacion, datos_dps, ruta_inicio, referrer, historial_navegacion, actividad_usuario')
              .eq('id', sesionId)
              .single()
          : Promise.resolve({ data: null, error: null }),
        db.from('subscriptions')
          .select('id, status, created_at, amount, currency, billing_frequency, external_subscription_id, certificado_id')
          .eq('contratacion_id', contratacionId)
          .maybeSingle(),
      ]);

      const sesion = sesionRes.error ? null : sesionRes.data;

      // Fallback: si no encontró por contratacion_id, buscar por ct.subscription_id
      let suscripcion = subRes.error ? null : subRes.data;
      if (!suscripcion && ct.subscription_id) {
        const { data: subFallback } = await db
          .from('subscriptions')
          .select('id, status, created_at, amount, currency, billing_frequency, external_subscription_id, certificado_id')
          .eq('id', ct.subscription_id)
          .maybeSingle();
        suscripcion = subFallback || null;
      }

      // 3. Buscar certificado: primero por certificado_id de la suscripcion, luego por subscription_id
      let certificado = null;
      if (suscripcion?.certificado_id) {
        const { data: cerData } = await db
          .from('certificados')
          .select('id, codigo, created_at, fecha_inicio_cobertura, fecha_termino_cobertura, email_enviado, email_enviado_at')
          .eq('id', suscripcion.certificado_id)
          .maybeSingle();
        certificado = cerData || null;
      } else if (suscripcion?.id) {
        const { data: cerData } = await db
          .from('certificados')
          .select('id, codigo, created_at, fecha_inicio_cobertura, fecha_termino_cobertura, email_enviado, email_enviado_at')
          .eq('subscription_id', suscripcion.id)
          .maybeSingle();
        certificado = cerData || null;
      }

      return {
        contratacion: {
          id: ct.id,
          codigo: ct.codigo,
          created_at: ct.created_at,
          estado: ct.estado,
          nombre: ct.nombre,
          rut: ct.rut,
          mail: ct.mail,
          datos_titular: ct.datos_titular,
        },
        cotizacion: cotizacion ? {
          id: cotizacion.id,
          codigo: cotizacion.codigo,
          created_at: cotizacion.created_at,
          expires_at: cotizacion.expires_at,
          resumen: cotizacion.resumen,
          plan_nombre: cotizacion.planes?.nombre,
          producto_nombre: cotizacion.planes?.producto?.nombre,
          compania_nombre: cotizacion.planes?.producto?.companias?.nombre_corto,
        } : null,
        sesion: sesion ? {
          id: sesion.id,
          created_at: sesion.created_at,
          ip: sesion.ip,
          canal: sesion.canal,
          user_agent: sesion.user_agent,
          ruta_inicio: sesion.ruta_inicio,
          referrer: sesion.referrer,
          historial_navegacion: sesion.historial_navegacion,
          actividad_usuario: sesion.actividad_usuario,
          datos_simulacion: sesion.datos_simulacion,
          datos_dps: sesion.datos_dps,
        } : null,
        suscripcion,
        certificado,
      };
    } catch (error) {
      logError('Error en getTrazabilidad:', error, { ignoreAbort: true });
      throw error;
    }
  }
}

// Exportar instancia única del servicio
const contratantesService = new ContratantesService();
export default contratantesService;

// Exportar también métodos individuales para compatibilidad
export const {
  getContrataciones,
  getContratacionById,
  getEstadisticas,
  actualizarEstado,
  getStats
} = contratantesService;
